import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useAlert } from '../context/AlertContext';
import './ChatTimers.css';

function ChatTimers() {
  const [timers, setTimers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTimer, setEditingTimer] = useState(null);
  const alert = useAlert();

  useEffect(() => {
    loadTimers();
  }, []);

  const loadTimers = async () => {
    try {
      const { data } = await axios.get('/api/timers');
      setTimers(data);
    } catch (error) {
      alert.error('Fehler beim Laden der Timer');
    }
  };

  const saveTimer = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const intervalMinutes = parseFloat(formData.get('interval_minutes'));
    const timerData = {
      platform: formData.get('platform'),
      name: formData.get('name'),
      message: formData.get('message'),
      interval: Math.round(intervalMinutes * 60), // Minuten → Sekunden
      enabled: formData.get('enabled') ? 1 : 0
    };

    try {
      if (editingTimer) {
        await axios.put(`/api/timers/${editingTimer.id}`, timerData);
        alert.success('Timer aktualisiert!');
      } else {
        await axios.post('/api/timers', timerData);
        alert.success('Timer erstellt!');
      }
      setShowModal(false);
      setEditingTimer(null);
      loadTimers();
    } catch (error) {
      alert.error('Fehler beim Speichern des Timers');
    }
  };

  const deleteTimer = async (id) => {
    if (window.confirm('Timer wirklich löschen?')) {
      try {
        await axios.delete(`/api/timers/${id}`);
        alert.success('Timer gelöscht');
        loadTimers();
      } catch (error) {
        alert.error('Fehler beim Löschen');
      }
    }
  };

  const toggleTimer = async (id, enabled) => {
    try {
      await axios.post(`/api/timers/${id}/toggle`, { enabled: !enabled });
      loadTimers();
    } catch (error) {
      alert.error('Fehler beim Umschalten');
    }
  };

  const openModal = (timer = null) => {
    setEditingTimer(timer);
    setShowModal(true);
  };

  const formatInterval = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = seconds / 60;
    if (mins < 60) return `${mins} Min`;
    const hours = mins / 60;
    return `${hours} Std`;
  };

  const getPlatformBadgeClass = (platform) => {
    if (platform.includes('twitch')) return 'twitch';
    if (platform.includes('youtube')) return 'youtube';
    if (platform.includes('discord')) return 'discord';
    return 'twitch';
  };

  return (
    <div className="chat-timers-page">
      <div className="page-header">
        <div>
          <h2>⏱️ Chat-Timer</h2>
          <p>Nachrichten die automatisch in bestimmten Abständen gepostet werden</p>
        </div>
        <button className="btn-success" onClick={() => openModal()}>+ Neuer Timer</button>
      </div>

      {timers.length === 0 ? (
        <div className="no-results">
          <div className="no-results-icon">⏱️</div>
          <p>Noch keine Chat-Timer vorhanden</p>
          <p className="no-results-sub">Erstelle einen Timer der automatisch Nachrichten in den Chat postet</p>
          <button className="btn-success" onClick={() => openModal()}>+ Ersten Timer erstellen</button>
        </div>
      ) : (
        <div className="timers-grid">
          {timers.map((timer) => (
            <div key={timer.id} className={`timer-card ${timer.enabled ? 'active' : 'inactive'}`}>
              <div className="timer-card-header">
                <div className="timer-title-row">
                  <h3>{timer.name}</h3>
                  <span className={`platform-badge ${getPlatformBadgeClass(timer.platform)}`}>
                    {timer.platform}
                  </span>
                </div>
                <div className="timer-toggle">
                  <button
                    className={`toggle-btn ${timer.enabled ? 'on' : 'off'}`}
                    onClick={() => toggleTimer(timer.id, timer.enabled)}
                    title={timer.enabled ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    {timer.enabled ? '● AN' : '○ AUS'}
                  </button>
                </div>
              </div>

              <div className="timer-message">
                <span className="timer-message-label">Nachricht:</span>
                <p>{timer.message}</p>
              </div>

              <div className="timer-meta">
                <div className="timer-interval">
                  <span className="meta-icon">🔄</span>
                  <span>Alle <strong>{formatInterval(timer.interval)}</strong></span>
                </div>
                <div className={`timer-status ${timer.enabled ? 'status-active' : 'status-inactive'}`}>
                  {timer.enabled ? '✓ Aktiv' : '✗ Inaktiv'}
                </div>
              </div>

              <div className="timer-actions">
                <button onClick={() => openModal(timer)}>✏️ Bearbeiten</button>
                <button className="btn-danger" onClick={() => deleteTimer(timer.id)}>🗑️ Löschen</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingTimer ? '⏱️ Timer bearbeiten' : '⏱️ Neuer Chat-Timer'}</h3>

            <form onSubmit={saveTimer}>
              <div className="form-group">
                <label>Name</label>
                <input
                  name="name"
                  defaultValue={editingTimer?.name}
                  placeholder="z.B. Discord Werbung"
                  required
                />
              </div>

              <div className="form-group">
                <label>Nachricht</label>
                <textarea
                  name="message"
                  defaultValue={editingTimer?.message}
                  placeholder="Die Nachricht die automatisch gepostet wird..."
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label>Intervall (Minuten)</label>
                <input
                  type="number"
                  name="interval_minutes"
                  defaultValue={editingTimer ? (editingTimer.interval / 60) : 30}
                  min="1"
                  step="0.5"
                  placeholder="z.B. 30 für alle 30 Minuten"
                  required
                />
                <span className="form-hint">Wie oft soll die Nachricht gepostet werden?</span>
              </div>

              <div className="form-group">
                <label>Plattform</label>
                <select name="platform" defaultValue={editingTimer?.platform || 'twitch-main'}>
                  <option value="twitch-main">Twitch Main</option>
                  <option value="twitch-247">Twitch 24/7</option>
                  <option value="youtube">YouTube</option>
                  <option value="discord">Discord</option>
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="enabled"
                    defaultChecked={editingTimer ? editingTimer.enabled : true}
                  />
                  Timer sofort aktivieren
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Abbrechen</button>
                <button type="submit" className="btn-success">💾 Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatTimers;
